import React, { Fragment, useState, useEffect } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { Button, Modal, ModalBody, ModalFooter, Input, Spinner } from "reactstrap";
import { useSelector } from "react-redux";
import apiRequest from "../../utils/apiRequest";
import { Breadcrumbs } from "../../AbstractElements";

const AllUsers = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionType, setActionType] = useState(null); // 'block', 'unblock', 'grant', or 'revoke'
  const [loading, setLoading] = useState(false);
  const token = useSelector((state) => state.auth.userToken);

  const fetchUsers = async () => {
    try {
      const response = await apiRequest('get', `${process.env.REACT_APP_API_BASE_URL}/admin/active-subadmins`, {}, token);
      setUsers(response.data.subAdmins);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const handleBlockToggle = async () => {
    try {
      setLoading(true);
      if (actionType === 'block') {
        await apiRequest('patch', `${process.env.REACT_APP_API_BASE_URL}/admin/block-user/${selectedUser._id}`, {}, token);
      } else if (actionType === 'unblock') {
        await apiRequest('post', `${process.env.REACT_APP_API_BASE_URL}/admin/unblock-user/${selectedUser._id}`, {}, token);
      }

      fetchUsers();
      setBlockModalOpen(false);
    } catch (error) {
      console.error("Error blocking/unblocking user:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccessToggle = async () => {
    try {
      setLoading(true);
      const url = actionType === 'grant'
        ? 'https://homesnappro-production.up.railway.app/api/v1/admin/access'
        : 'https://homesnappro-production.up.railway.app/api/v1/admin/revoke-drive-access';

      await apiRequest('post', url, { subAdminId: selectedUser._id }, token);

      fetchUsers();
      setAccessModalOpen(false);
    } catch (error) {
      console.error(`Error ${actionType === 'grant' ? 'granting' : 'revoking'} drive access:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/admin/delete-user/${selectedUser._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(users.filter((user) => user._id !== selectedUser._id));
      setModalOpen(false);
    } catch (error) {
      console.error("Error deleting user:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockModal = (user, type) => {
    setSelectedUser(user);
    setActionType(type);
    setBlockModalOpen(true);
  };

  const handleAccessModal = (user, type) => {
    setSelectedUser(user);
    setActionType(type);
    setAccessModalOpen(true);
  };

  const handleDeleteModal = (user) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const customColumns = [
    {
      name: 'Username',
      selector: (row) => row.userName,
      sortable: true,
      center: false,
    },
    {
      name: 'Email',
      selector: (row) => row.email,
      sortable: true,
      center: false,
    },

    {
      name: "Block",
      cell: (row) => (
        <Button
          color={row.isBLock ? "success" : "danger"}
          onClick={() => handleBlockModal(row, row.isBLock ? 'unblock' : 'block')}
        >
          {row.isBLock ? "Unblock" : "Block"}
        </Button>
      ),
      button: true,
      center: true,
      minWidth: "200px",
      maxWidth: "300px",
    },
    {
      name: "Drive Access",
      cell: (row) => (
        <Button
          color={row.isDriveAccess ? "warning" : "primary"}
          onClick={() => handleAccessModal(row, row.isDriveAccess ? 'revoke' : 'grant')}
        >
          {row.isDriveAccess ? "Revoke Access" : "Grant Access"}
        </Button>
      ),
      button: true,
      center: true,
      minWidth: "200px",
      maxWidth: "300px",
    },
  ];

  return (
    <Fragment>
            <Breadcrumbs mainTitle="Sub-Admins" parent="Dashboard" title="All Admins"/>

      {/* <div className="container-fluid">
        <div className="row">
          <div className="col-md-6">
            <Input
              type="text"
              placeholder="Search by name"
              value={searchTerm}
              onChange={handleSearch}
              className="p-2"
            />
          </div>
        </div>
      </div> */}

      <div className="table-responsive support-table mt-3">
        <DataTable
          columns={customColumns}
          data={users.filter(user => user.userName.toLowerCase().includes(searchTerm.toLowerCase()))}
          striped={true}
          center={true}
          pagination
          className="p-2"
          responsive={true}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
        <ModalBody>
          Are you sure you want to delete this user?
        </ModalBody>
        <ModalFooter>
          <Button color={"black"} onClick={() => setModalOpen(!modalOpen)}>
            Cancel
          </Button>
          <Button color={"danger"} onClick={handleDeleteUser}>
            {loading ? <Spinner size="sm" /> : "Delete"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Block/Unblock Confirmation Modal */}
      <Modal isOpen={blockModalOpen} toggle={() => setBlockModalOpen(!blockModalOpen)}>
        <ModalBody>
          Are you sure you want to {actionType} this user?
        </ModalBody>
        <ModalFooter>
          <Button color={"black"} onClick={() => setBlockModalOpen(!blockModalOpen)}>
            Cancel
          </Button>
          <Button color={actionType === 'block' ? "danger" : "success"} onClick={handleBlockToggle}>
            {loading ? <Spinner size="sm" /> : (actionType === 'block' ? "Block" : "Unblock")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Grant/Revoke Drive Access Confirmation Modal */}
      <Modal isOpen={accessModalOpen} toggle={() => setAccessModalOpen(!accessModalOpen)}>
        <ModalBody>
          Are you sure you want to {actionType === 'grant' ? 'grant' : 'revoke'} drive access to this user?
        </ModalBody>
        <ModalFooter>
          <Button color={"black"} onClick={() => setAccessModalOpen(!accessModalOpen)}>
            Cancel
          </Button>
          <Button color={actionType === 'grant' ? "primary" : "warning"} onClick={handleAccessToggle}>
            {loading ? <Spinner size="sm" /> : (actionType === 'grant' ? "Grant Access" : "Revoke Access")}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default AllUsers;
